/* eslint-disable @typescript-eslint/naming-convention */
import React, { useCallback, useEffect, useState } from 'react';

import type { EventSchedule } from '@swf/cms';
import { CMSHeading } from '@swf/cms';
import LoadingIndicator from '@swf/components/LoadingIndicator/LoadingIndicator';

import { xmlToJson } from '../../utils/xml';

import styles from './CMSEventSchedule.scss';
import PDF from './assets/pdf.svg';
import EventDetailsFilter from './components/EventDetails/EventDetailsFilter';
import EventDetailsTable from './components/EventDetails/EventDetailsTable';
import type { EventDetails } from './components/EventDetails/EventDetailsTable';
import type { Stop, Tour } from './components/EventResults/EventResultFilter';
import EventResultFilter from './components/EventResults/EventResultFilter';
import type { EventResult } from './components/EventResults/EventResultTable';
import EventResultTable from './components/EventResults/EventResultTable';

interface EventsData {
  stops: {
    stop: {
      attributes: { id: string; name: string };
      tour: { attributes: { url: string; title: string } };
    }[];
  };
}

interface ScheduleData {
  stop: {
    attributes: { name: string; schedulepdfurl: string; id: string };
    data: { event: EventData[] };
    winners?: { winner: { attributes: { description: string } }[] };
  };
}

interface EventData {
  buyin: { attributes: { cost: string; fee: string; house: string } };
  reentry: { attributes: { cost: string; fee: string; house: string } };
  bounty: { attributes: { cost: string; house: string } };
  attributes: {
    title: string;
    name: string;
    startdate: string;
    finishdate: string;
    registrationfinishdate: string;
    startingstack: string;
    gametype: string;
    prizepool: string;
    totalentries: string;
    entries: string;
    cap: string;
    payoutsurl: string;
    totalplayersleft: string;
    left: string;
    seatdrawurl: string;
    status: string;
    isfirstday: string;
    buyin: string;
    dayid: string;
    ordid: string;
    icsurl: string;
    sopurl: string;
    winner: string;
    winneramount: string;
    tlbsourceurl: string;
    ['sop-description']: string;
  };
}

enum GridType {
  EventDetails = 'Event Details',
  EventResult = 'Event Results'
}

const CMSEventSchedule: React.FC<EventSchedule> = ({ events_xml_url, event_id, type }) => {
  const [events, setEvents] = useState<EventDetails[] | EventResult[]>([]);
  const [eventsDataResponse, setEventsDataResponse] = useState<EventsData>();
  const [filteredEvents, setFilteredEvents] = useState<EventDetails[] | EventResult[]>([]);
  const [tours, setTours] = useState<Tour[]>([]);
  const [selectedEventId, setSelectedEventId] = useState(event_id);
  const [selectedTourName, setSelectedTourName] = useState('');
  const [selectedStopName, setSelectedStopName] = useState('');
  const [loading, setLoading] = useState(false);

  const getDayId = (dayId: unknown) => {
    if (!dayId) {
      return '';
    }

    if (isNaN(dayId as number)) {
      return `(${dayId})`;
    }

    return `(day ${dayId})`;
  };

  const extractToursList = useCallback((data: EventsData) => {
    const tours = new Map<string, Stop[]>();
    data.stops.stop.forEach(({ attributes, tour }) => {
      if (!tours.get(tour.attributes.title)) {
        tours.set(tour.attributes.title, []);
      }

      tours.get(tour.attributes.title)?.push({ name: attributes.name, id: attributes.id });
    });

    const result: Tour[] = [];
    tours.forEach((stops, name) => {
      result.push({ name, stops });
    });

    return result;
  }, []);

  const extractEventResultList = useCallback((data: ScheduleData) => {
    const result: EventResult[] = [];
    data.stop.data.event.forEach((item) => {
      if (item.attributes.tlbsourceurl && item.attributes.winner) {
        result.push({
          title: item.attributes.name,
          prizePool: item.attributes.prizepool,
          players: item.attributes.totalentries,
          startDate: new Date(item.attributes.startdate),
          orderId: item.attributes.ordid,
          buyin: item.attributes.buyin,
          winner: {
            name: item.attributes.winner,
            prize: item.attributes.winneramount,
            tableUrl: item.attributes.tlbsourceurl
          }
        } as EventResult);
      }
    });

    return result.sort((a, b) => parseInt(a.orderId) - parseInt(b.orderId));
  }, []);

  const extractEventDetailsList = useCallback((data: ScheduleData) => {
    const result = data.stop.data.event.map((item: EventData) => ({
      title: item.attributes.title,
      startDate: new Date(item.attributes.startdate),
      endDate: new Date(item.attributes.finishdate),
      registrationEndDate: new Date(item.attributes.registrationfinishdate),
      startingStack: isNaN(parseInt(item.attributes.startingstack))
        ? ''
        : item.attributes.startingstack,
      gameType: item.attributes.gametype,
      status: item.attributes.status,
      dayId: getDayId(item.attributes.dayid),
      orderId: `#${item.attributes.ordid}`,
      icsurl: item.attributes.icsurl,
      sopUrl: item.attributes.sopurl,
      buyin: item.attributes.buyin,
      sopDescription: item.attributes['sop-description'],
      entry: item.buyin.attributes,
      reEntry: item.reentry.attributes,
      bounty: item.bounty.attributes,
      cap: parseInt(item.attributes.cap),
      prizePool: item.attributes.prizepool,
      entries: item.attributes.entries,
      payoutsUrl: item.attributes.payoutsurl,
      seatDrawUrl: item.attributes.seatdrawurl,
      totalPlayersLeft: item.attributes.dayid.startsWith('1')
        ? item.attributes.left
        : item.attributes.totalplayersleft,
      totalEntries: item.attributes.dayid.startsWith('1')
        ? item.attributes.entries
        : item.attributes.totalentries
    })) as EventDetails[];
    return result.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
  }, []);

  useEffect(() => {
    const fetchXML = async () => {
      if (!events_xml_url) {
        return;
      }

      setLoading(true);

      try {
        const eventsDataResponse = await (await fetch(events_xml_url, { signal })).text();
        const eventsXml = new DOMParser().parseFromString(eventsDataResponse, 'text/xml');
        const eventsData = xmlToJson(eventsXml) as EventsData;
        setEventsDataResponse(eventsData);
        if (type === GridType.EventResult) {
          setTours(extractToursList(eventsData));
        }
      } catch {
        setLoading(false);
      }
    };

    const abortController = new AbortController();
    const { signal } = abortController;

    fetchXML();

    return () => abortController.abort();
  }, [events_xml_url, extractToursList, type]);

  useEffect(() => {
    const fetchXML = async () => {
      if (!eventsDataResponse) {
        return;
      }

      setLoading(true);

      try {
        const event = eventsDataResponse?.stops.stop.find(
          (item) => item.attributes.id === selectedEventId
        );

        if (event?.tour.attributes.url) {
          const scheduleDataResponse = await (
            await fetch(event?.tour.attributes.url, { signal })
          ).text();
          const scheduleXml = new DOMParser().parseFromString(scheduleDataResponse, 'text/xml');
          const scheduleData = xmlToJson(scheduleXml) as ScheduleData;
          if (type === GridType.EventDetails) {
            setEvents(extractEventDetailsList(scheduleData));
          } else {
            setEvents(extractEventResultList(scheduleData));
          }
        }
      } catch (err) {
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    const abortController = new AbortController();
    const { signal } = abortController;

    fetchXML();

    return () => abortController.abort();
  }, [
    event_id,
    eventsDataResponse,
    extractEventDetailsList,
    extractEventResultList,
    extractToursList,
    selectedEventId,
    type
  ]);

  const onPrintScreen = () => {
    window.print();
  };

  const onFilter = (id: string, tour: string, stop: string) => {
    setSelectedEventId(id);
    setSelectedTourName(tour);
    setSelectedStopName(stop);
  };

  return (
    <>
      <CMSHeading
        title={type === GridType.EventDetails ? 'Tournament Schedule' : 'Tournament Results'}
        level="h2"
        size="m"
        className={styles.title}
        hero={false}
        alignment={null}
        target={null}
      />
      <PDF className={styles.pdf} onClick={onPrintScreen} />
      {type === GridType.EventDetails && (
        <>
          <EventDetailsFilter events={events as EventDetails[]} onFilter={setFilteredEvents} />
          <EventDetailsTable events={filteredEvents as EventDetails[]} />
        </>
      )}
      {type === GridType.EventResult && (
        <>
          <EventResultFilter tours={tours} onFilter={onFilter} />
          {loading && (
            <div className={styles.loadingIndicator}>
              <LoadingIndicator loading={loading} />
            </div>
          )}
          {!loading && (
            <EventResultTable
              events={events as EventResult[]}
              tour={selectedTourName}
              stop={selectedStopName}
            />
          )}
        </>
      )}
    </>
  );
};

export default CMSEventSchedule;
